import React, {useCallback, useContext} from 'react';
import {useNavigate} from 'react-router-dom';


import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';

import {CreateUserStep} from '../../components/create-user-step/create-user-step';
import {CREATE_USER_STEPS} from '../../constants';

import {UserSettingsController} from './user-settings.controller';

import type {ConfirmationContextType} from 'src/contexts/confirmation-context';

type Props = {
    userId: number,
    canSetupAdminRole: boolean,
    canViewLimits: boolean,
    onBack: () => void,
    onNext?: () => void,
    onRedirectToStep: (step: CREATE_USER_STEPS) => void,
};

export const CreateUserSettingsStep = (props: Props) => {
    const {
        userId,
        canSetupAdminRole,
        canViewLimits,
        onBack,
        onNext,
        onRedirectToStep,
    } = props;
    const navigate = useNavigate();
    const {onShowModal} = useContext<ConfirmationContextType>(ConfirmationContext);
    const {userCreateSubmit} = useEventTracking();

    const onDone = useCallback(() => {
        userCreateSubmit({place: 'User Limits', action: 'Done'});
        navigate(`/profile/user/${userId}`);
    }, [navigate, userId, userCreateSubmit]);

    const onClose = useCallback(() => {
        userCreateSubmit({place: 'User Settings', action: 'Close'});
        navigate(`/profile/user/${userId}`);
    }, [userId, navigate, userCreateSubmit]);

    return (
        <ValidationContextProvider>
            <UserSettingsController userId={userId} onProceed={onNext || onDone}>
                {({content, onSave, busy, loadingError, isChunkChanged}) => (
                    <CreateUserStep
                        selectedStep={CREATE_USER_STEPS.SETTINGS}
                        onChangeStep={step => {
                            if (isChunkChanged) {
                                onShowModal(() => onRedirectToStep(step));
                            } else {
                                onRedirectToStep(step);
                            }
                        }}
                        loadingError={loadingError}
                        canViewLimits={canViewLimits}
                        canSetupAdminRole={canSetupAdminRole}
                        buttonsBlock={[
                            <Button key="Close" variant="Type.TERTIARY" onClick={onClose}>Close</Button>,
                            <Button
                                key="Back"
                                variant="Type.SECONDARY"
                                onClick={() => {
                                    if (isChunkChanged) {
                                        onShowModal(onBack);
                                    } else {
                                        onBack();
                                    }
                                }}>
                                Back
                            </Button>,
                            <Button key="Save" variant="Type.PRIMARY" busy={busy} onClick={() => onSave()}>
                                {onNext ? 'Save & Continue' : 'Finish'}
                            </Button>,
                        ]}
                        content={content}
                    />
                )}
            </UserSettingsController>
        </ValidationContextProvider>
    );
};
